import { useNavigate } from "@solidjs/router";
import type { Notification } from "@storytell/client";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { TbTrash } from "solid-icons/tb";
import { type Component, Show } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";
import { getNotificationDisplay, getNotificationHref, type NotificationDisplay } from "../notificationDisplay";
import { getNotificationIcon } from "../notificationIcon";

dayjs.extend(relativeTime);

export const NotificationItem: Component<{ notification: Notification }> = (props) => {
  const wire = useWire();
  const navigate = useNavigate();
  const { t } = useI18n();

  // Plain functions, not memos: memo reads can yield undefined mid-transition
  // (ENG-6676), and icon() is read unguarded below.
  const display = () => getNotificationDisplay(props.notification);
  const icon = () => getNotificationIcon(props.notification);
  const withRole = (d: NotificationDisplay) => (d.roleKey ? { ...d.params, role: t(d.roleKey) } : d.params);
  const title = () => {
    const d = display();
    return d ? t(d.titleKey, withRole(d)) : props.notification.fallbackTitle;
  };
  const body = () => {
    const d = display();
    return d ? t(d.bodyKey, withRole(d)) : props.notification.fallbackBody;
  };
  const isUnread = () => props.notification.readAt === null;

  const handleOpen = () => {
    trackFeature({ feature: "notifications", action: "click", via: "button" });
    wire.notifications.markRead(props.notification.id);
    wire.ui.layout.setNotificationsOpen(false);
    navigate(getNotificationHref(props.notification));
  };

  const handleDelete = (e: MouseEvent) => {
    e.stopPropagation();
    trackFeature({ feature: "notifications", action: "delete", via: "button" });
    wire.notifications.remove(props.notification.id);
  };

  return (
    <div class="group relative flex items-stretch">
      <button
        type="button"
        onClick={handleOpen}
        class="flex min-w-0 flex-1 items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-panel-hover focus:outline-none focus-visible:bg-panel-hover"
      >
        {/* The unread dot rides the icon's corner instead of owning a column,
            so read items don't carry an empty gutter. */}
        <span class="relative mt-0.5 shrink-0" aria-hidden="true">
          <Dynamic component={icon().icon} class={`size-4 ${icon().tone}`} aria-hidden={true} />
          <Show when={isUnread()}>
            <span class="absolute -top-1 -right-1 size-2 rounded-full bg-brand" />
          </Show>
        </span>
        <span class="min-w-0 flex-1">
          <span
            class="block truncate text-sm text-primary-text"
            classList={{ "font-semibold": isUnread(), "font-medium": !isUnread() }}
          >
            {title()}
          </span>
          <span class="mt-0.5 line-clamp-2 text-xs text-secondary-text">{body()}</span>
          <span class="mt-1 block text-[0.7rem] text-muted-text">{dayjs(props.notification.createdAt).fromNow()}</span>
        </span>
      </button>
      <button
        type="button"
        onClick={handleDelete}
        aria-label={t("notifications.item.delete")}
        class="absolute top-2 right-2 rounded-md p-1.5 text-muted-text opacity-0 transition-opacity group-hover:opacity-100 hover:bg-inset hover:text-primary-text focus:opacity-100 focus:outline-none"
      >
        <TbTrash class="size-4" aria-hidden="true" />
      </button>
    </div>
  );
};
